// RecurringExpensesIntegrationManager.jsx - FIXED VERSION (Focus and Date Issues)
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Receipt, DollarSign, CreditCard, RefreshCw, Plus, Search, Filter,
    CheckCircle, XCircle, Clock, AlertCircle, Eye, Edit, Trash2,
    Download, Upload, BarChart3, TrendingUp, PlayCircle, PauseCircle,
    Copy, Calendar, Settings, Zap, Target, ArrowRight, Building,
    Calculator, Repeat, Car, Coffee, Wifi, Briefcase, Home, MapPin,
    User, FolderOpen, AlertTriangle, Loader
} from 'lucide-react';

const RecurringExpensesIntegrationManager = () => {
    const [activeTab, setActiveTab] = useState('overview');
    const [recurringExpenses, setRecurringExpenses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [selectedExpenses, setSelectedExpenses] = useState([]);
    const [validationResults, setValidationResults] = useState({});

    // Account data states
    const [expenseAccounts, setExpenseAccounts] = useState([]);
    const [paymentAccounts, setPaymentAccounts] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [projects, setProjects] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [taxes, setTaxes] = useState([]);

    // Loading states for different data types
    const [loadingStates, setLoadingStates] = useState({
        expenses: false,
        expenseAccounts: false,
        paymentAccounts: false,
        employees: false,
        projects: false,
        customers: false,
        taxes: false
    });

    const [syncProgress, setSyncProgress] = useState(null);
    const [syncing, setSyncing] = useState(null);
    const [statistics, setStatistics] = useState({});
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showSyncModal, setShowSyncModal] = useState(false);
    const [showValidationModal, setShowValidationModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [currentExpense, setCurrentExpense] = useState(null);
    const [errors, setErrors] = useState({});

    // FIXED: Use useRef to prevent re-rendering and maintain form stability
    const formRef = useRef({
        recurrence_name: '',
        expense_type: 'business',
        category: 'general',
        description: '',
        account_id: '',
        account_name: '',
        paid_through_account_id: '',
        paid_through_account_name: '',
        amount: '',
        start_date: '',
        end_date: '',
        recurrence_frequency: 'monthly',
        repeat_every: 1,
        currency_code: 'INR',
        is_billable: false,
        is_reimbursable: false,
        employee_id: '',
        employee_name: '',
        project_id: '',
        project_name: '',
        customer_id: '',
        customer_name: '',
        receipt_required: false,
        auto_submit: false,
        notes: '',
        tags: []
    });

    const [recurringExpenseForm, setRecurringExpenseForm] = useState({
        recurrence_name: '',
        expense_type: 'business',
        category: 'general',
        description: '',
        account_id: '',
        account_name: '',
        paid_through_account_id: '',
        paid_through_account_name: '',
        amount: '',
        start_date: '',
        end_date: '',
        recurrence_frequency: 'monthly',
        repeat_every: 1,
        currency_code: 'INR',
        is_billable: false,
        is_reimbursable: false,
        employee_id: '',
        employee_name: '',
        project_id: '',
        project_name: '',
        customer_id: '',
        customer_name: '',
        receipt_required: false,
        auto_submit: false,
        notes: '',
        tags: []
    });
    const [formVersion, setFormVersion] = useState(0); // Force re-render when needed

    // FIXED: Stable form update function that doesn't cause re-renders
    const updateFormField = useCallback((field, value) => {
        setRecurringExpenseForm(prev => {
            // Only update if value actually changed to prevent unnecessary re-renders
            if (prev[field] === value) return prev;
            return { ...prev, [field]: value };
        });
    }, []);

    // FIXED: Reset form function
    // FIXED: Batch form update to prevent multiple re-renders
    const updateMultipleFormFields = useCallback((updates) => {
        setRecurringExpenseForm(prev => ({ ...prev, ...updates }));
    }, []);

    // FIXED: Reset form with single state update
    const resetForm = useCallback(() => {
        const defaultForm = {
            recurrence_name: '',
            expense_type: 'business',
            category: 'general',
            description: '',
            account_id: '',
            account_name: '',
            paid_through_account_id: '',
            paid_through_account_name: '',
            amount: '',
            start_date: '',
            end_date: '',
            recurrence_frequency: 'monthly',
            repeat_every: 1,
            currency_code: 'INR',
            is_billable: false,
            is_reimbursable: false,
            employee_id: '',
            employee_name: '',
            project_id: '',
            project_name: '',
            customer_id: '',
            customer_name: '',
            receipt_required: false,
            auto_submit: false,
            notes: '',
            tags: []
        };

        setRecurringExpenseForm(defaultForm);
    }, []);

    const categoryKeywords = useMemo(() => ({
        rent: { category: 'rent', keywords: ['rent', 'lease', 'office space', 'facility', 'premises'] },
        travel: { category: 'travel', keywords: ['travel', 'flight', 'taxi', 'uber', 'transport', 'fuel', 'gas', 'petrol'] },
        utilities: { category: 'utilities', keywords: ['electricity', 'water', 'internet', 'wifi', 'phone', 'mobile', 'broadband'] },
        meals: { category: 'meals', keywords: ['food', 'lunch', 'dinner', 'restaurant', 'meal', 'catering'] },
        office: { category: 'office', keywords: ['stationery', 'supplies', 'printer', 'paper', 'ink', 'software'] },
        communication: { category: 'communication', keywords: ['phone', 'mobile', 'telecom', 'communication'] },
        professional: { category: 'professional', keywords: ['consultant', 'legal', 'audit', 'professional', 'service'] },
        general: { category: 'general', keywords: ['misc', 'other', 'general', 'expense'] }
    }), []);

    const expenseAccountKeywords = useMemo(() => ({
        'Office Rent': ['rent', 'office rent', 'facility'],
        'Travel Expenses': ['travel', 'transportation'],
        'Utilities': ['electricity', 'water', 'internet', 'utilities'],
        'Office Supplies': ['stationery', 'supplies', 'office'],
        'Meals & Entertainment': ['food', 'meal', 'entertainment'],
        'Professional Services': ['consultant', 'legal', 'professional'],
        'Communication': ['phone', 'telecom', 'communication'],
        'Fuel & Vehicle': ['fuel', 'gas', 'vehicle', 'car']
    }), []);

    // FIXED: Use actual database status with correct column names
    const canEdit = useCallback((expense) => {
        const status = expense.approver_status || expense.approvalInfo?.status;
        console.log('Edit check for expense', expense.id, 'status:', status);
        // Only allow editing if explicitly approved
        return status === 'approved';
    }, []);

    const canSync = useCallback((expense) => {
        const status = expense.approver_status || expense.approvalInfo?.status;
        console.log('Sync check for expense', expense.id, 'status:', status);
        // Only allow syncing if explicitly approved
        return status === 'approved';
    }, []);

    const getApprovalStatusDisplay = useCallback((expense) => {
        // FIXED: Use actual database status, with correct column names
        const status = expense.approver_status || expense.approvalInfo?.status;
        console.log('Status display for expense', expense.id, '- Database status:', expense.approver_status, '- User type:', expense.user_type, '- Final status:', status);

        if (status === 'approved') {
            return (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Approved
                    {expense.approved_by && <span className="ml-1">by {expense.approved_by}</span>}
                </span>
            );
        }

        if (status === 'rejected') {
            return (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    <XCircle className="w-3 h-3 mr-1" />
                    Rejected
                </span>
            );
        }

        // Default to pending approval
        return (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                <Clock className="w-3 h-3 mr-1" />
                Pending Approval
            </span>
        );
    }, []);

    // FIXED: AI suggestion function with better logic
    const getAISuggestionsForExpense = useCallback((recurrenceName, description = '') => {
        const text = `${recurrenceName} ${description}`.toLowerCase();

        // Suggest category
        let suggestedCategory = 'general';
        for (const [category, config] of Object.entries(categoryKeywords)) {
            if (config.keywords.some(keyword => text.includes(keyword))) {
                suggestedCategory = category;
                break;
            }
        }

        // Suggest expense account
        let suggestedAccountName = '';
        let suggestedAccountId = '';
        for (const [accountName, keywords] of Object.entries(expenseAccountKeywords)) {
            if (keywords.some(keyword => text.includes(keyword))) {
                const account = expenseAccounts.find(acc =>
                    acc.account_name.toLowerCase().includes(accountName.toLowerCase()) ||
                    keywords.some(k => acc.account_name.toLowerCase().includes(k))
                );
                if (account) {
                    suggestedAccountName = account.account_name;
                    suggestedAccountId = account.account_id;
                    break;
                }
            }
        }

        return {
            category: suggestedCategory,
            account_name: suggestedAccountName,
            account_id: suggestedAccountId
        };
    }, [categoryKeywords, expenseAccountKeywords, expenseAccounts]);

    // FIXED: Handle recurrence name change with AI suggestions
    const handleRecurrenceNameChange = useCallback((value) => {
        updateFormField('recurrence_name', value);

        // Auto-suggest based on name
        if (value.length > 3) {
            const suggestions = getAISuggestionsForExpense(value);

            // Only update if current values are empty/default
            if (recurringExpenseForm.category === 'general' && suggestions.category !== 'general') {
                updateFormField('category', suggestions.category);
            }

            if (!recurringExpenseForm.account_id && suggestions.account_id) {
                updateFormField('account_id', suggestions.account_id);
                updateFormField('account_name', suggestions.account_name);
            }
        }
    }, [getAISuggestionsForExpense, recurringExpenseForm.category, recurringExpenseForm.account_id, updateFormField]);

    // FIXED: Properly format dates for different contexts
    const formatDateForInput = useCallback((dateString) => {
        if (!dateString) return '';

        try {
            // Handle various date formats
            let date;
            if (typeof dateString === 'string') {
                // If it's already in YYYY-MM-DD format, return as is
                if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
                    return dateString;
                }
                // Handle ISO date strings
                if (dateString.includes('T')) {
                    date = new Date(dateString);
                } else {
                    date = new Date(dateString + 'T00:00:00.000Z');
                }
            } else {
                date = new Date(dateString);
            }

            if (isNaN(date.getTime())) {
                console.warn(`Invalid date: ${dateString}`);
                return '';
            }

            // Return in YYYY-MM-DD format for HTML date input
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');

            return `${year}-${month}-${day}`;
        } catch (error) {
            console.error('Date formatting error:', error);
            return '';
        }
    }, []);

    // FIXED: Map database frequency to form frequency
    const mapDbFrequencyToForm = useCallback((dbFrequency, repeatEvery = 1) => {
        if (!dbFrequency) return 'monthly';

        // Handle quarterly special case first
        if (dbFrequency === 'months' && repeatEvery === 3) {
            return 'quarterly';
        }

        const frequencyMap = {
            'days': 'daily',
            'weeks': 'weekly',
            'months': 'monthly',
            'years': 'yearly'
        };

        return frequencyMap[dbFrequency] || dbFrequency || 'monthly';
    }, []);

    // FIXED: Map form frequency to database frequency
    const mapFormFrequencyToDb = useCallback((formFrequency) => {
        const frequencyMap = {
            'daily': 'days',
            'weekly': 'weeks',
            'monthly': 'months',
            'quarterly': 'months', // Will be handled by repeat_every = 3
            'yearly': 'years'
        };
        return frequencyMap[formFrequency] || 'months';
    }, []);

    const API_BASE_URL = 'https://api.zapbooksai.subraps.com/api';

    // Organization state
    const [currentOrganization, setCurrentOrganization] = useState({
        organization_id: '',
        name: ''
    });

    const getAuthHeaders = () => {
        const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
        return {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
        };
    };

    // Fetch current organization
    const fetchCurrentOrganization = async () => {
        try {
            console.log('🔍 Fetching current organization...');
            const response = await fetch(`${API_BASE_URL}/token/status`, {
                headers: getAuthHeaders()
            });
            const result = await response.json();

            if (result.success && result.data.current_organization) {
                const org = result.data.current_organization;
                console.log('✅ Current organization fetched:', org);
                setCurrentOrganization(org);
                return org.organization_id;
            } else {
                console.error('❌ No current organization found');
                throw new Error('No current organization found');
            }
        } catch (error) {
            console.error('❌ Error fetching current organization:', error);
        }
    };

    // Initialize data
    useEffect(() => {
        const initializeComponent = async () => {
            try {
                await fetchCurrentOrganization();
            } catch (error) {
                console.error('Failed to initialize component:', error);
            }
        };
        initializeComponent();
    }, []);

    const expenseCategories = [
        { id: 'travel', name: 'Travel & Transportation', icon: Car, color: 'blue' },
        { id: 'meals', name: 'Meals & Entertainment', icon: Coffee, color: 'green' },
        { id: 'office', name: 'Office Supplies', icon: Briefcase, color: 'purple' },
        { id: 'utilities', name: 'Utilities', icon: Wifi, color: 'orange' },
        { id: 'rent', name: 'Rent & Facilities', icon: Home, color: 'teal' },
        { id: 'communication', name: 'Communication', icon: MapPin, color: 'indigo' },
        { id: 'professional', name: 'Professional Services', icon: Building, color: 'gray' },
        { id: 'general', name: 'General Expenses', icon: Receipt, color: 'slate' }
    ];

    // Helper function to update loading states
    const updateLoadingState = (key, value) => {
        setLoadingStates(prev => ({ ...prev, [key]: value }));
    };

    // Helper function to handle API errors
    const handleApiError = (error, context) => {
        console.error(`❌ Error in ${context}:`, error);
        setErrors(prev => ({ ...prev, [context]: error.message }));
    };

    // Fetch recurring expenses with validation data
    const fetchRecurringExpenses = async () => {
        try {
            updateLoadingState('expenses', true);
            setErrors(prev => ({ ...prev, expenses: null }));
            console.log('🔄 Fetching recurring expenses...');

            const response = await fetch(`${API_BASE_URL}/recurring-expenses/with-validation-data`, {
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();

            if (data.success) {
                setRecurringExpenses(data.data || []);
                setStatistics(data.summary || {});
                console.log(`✅ Fetched ${data.data?.length || 0} recurring expenses`);
            } else {
                throw new Error(data.message || 'Failed to fetch recurring expenses');
            }
        } catch (error) {
            handleApiError(error, 'expenses');
            setRecurringExpenses([]);
        } finally {
            updateLoadingState('expenses', false);
        }
    };

    // Fetch expense accounts
    const fetchExpenseAccounts = async () => {
        try {
            updateLoadingState('expenseAccounts', true);
            setErrors(prev => ({ ...prev, expenseAccounts: null }));
            console.log('🔄 Fetching expense accounts...');

            const response = await fetch(`${API_BASE_URL}/recurring-expenses/expense-accounts`, {
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            console.log('Expense accounts response:', data);

            if (data.success && Array.isArray(data.accounts)) {
                setExpenseAccounts(data.accounts);
                console.log('✅ Expense accounts loaded:', data.accounts.length);
            } else {
                console.error('❌ Invalid expense accounts data structure:', data);
                setExpenseAccounts([]);
            }
        } catch (error) {
            handleApiError(error, 'expenseAccounts');
            setExpenseAccounts([]);
        } finally {
            updateLoadingState('expenseAccounts', false);
        }
    };

    // Fetch payment accounts with enhanced error handling
    const fetchPaymentAccounts = async () => {
        try {
            updateLoadingState('paymentAccounts', true);
            setErrors(prev => ({ ...prev, paymentAccounts: null }));
            console.log('🔄 Fetching payment accounts...');

            const response = await fetch(`${API_BASE_URL}/recurring-expenses/payment-accounts`, {
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            console.log('Payment accounts response:', data);

            if (data.success && Array.isArray(data.accounts)) {
                // Sort accounts by type for better UX
                const sortedAccounts = data.accounts.sort((a, b) => {
                    const typeOrder = { 'bank': 1, 'cash': 2, 'credit_card': 3, 'other_current_asset': 4 };
                    return (typeOrder[a.account_type] || 5) - (typeOrder[b.account_type] || 5);
                });

                setPaymentAccounts(sortedAccounts);
                console.log('✅ Payment accounts loaded:', sortedAccounts.length);

                // Show data source info in development
                if (data.source) {
                    console.log(`📊 Payment accounts source: ${data.source}`);
                }
            } else {
                console.error('❌ Invalid payment accounts data structure:', data);
                setPaymentAccounts([]);
                throw new Error('Invalid data structure received');
            }
        } catch (error) {
            handleApiError(error, 'paymentAccounts');
            setPaymentAccounts([]);

            // Set fallback data if API completely fails
            const fallbackAccounts = [
                { account_id: 'fallback_bank_001', account_name: 'Primary Bank Account (Fallback)', account_type: 'bank', account_code: 'FB001' },
                { account_id: 'fallback_cash_001', account_name: 'Petty Cash (Fallback)', account_type: 'cash', account_code: 'FC001' }
            ];

            setTimeout(() => {
                if (paymentAccounts.length === 0) {
                    console.log('🔄 Using fallback payment accounts...');
                    setPaymentAccounts(fallbackAccounts);
                }
            }, 1000);
        } finally {
            updateLoadingState('paymentAccounts', false);
        }
    };

    // FIXED: Stable input component that doesn't lose focus
    const StableInput = React.memo(({
        label,
        value,
        onChange,
        type = "text",
        required = false,
        placeholder,
        disabled = false,
        fieldName,
        min,
        max,
        step,
        rows,
        ...props
    }) => {
        const inputRef = useRef(null);
        const [localValue, setLocalValue] = useState(value || '');

        // Update local value when prop changes (for external updates)
        useEffect(() => {
            if (value !== localValue) {
                setLocalValue(value || '');
            }
        }, [value]);

        const handleChange = useCallback((e) => {
            const newValue = e.target.value;
            setLocalValue(newValue);
            onChange(newValue);
        }, [onChange]);

        const handleBlur = useCallback(() => {
            // Ensure final value is synced on blur
            if (onChange && localValue !== value) {
                onChange(localValue);
            }
        }, [onChange, localValue, value]);

        const inputElement = type === 'textarea' ? (
            <textarea
                ref={inputRef}
                value={localValue}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={placeholder}
                disabled={disabled}
                rows={rows || 3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 resize-none"
                required={required}
                autoComplete="off"
                {...props}
            />
        ) : (
            <input
                ref={inputRef}
                type={type}
                value={localValue}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={placeholder}
                disabled={disabled}
                min={min}
                max={max}
                step={step}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100"
                required={required}
                autoComplete="off"
                {...props}
            />
        );

        return (
            <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                    {label} {required && <span className="text-red-500">*</span>}
                </label>
                {inputElement}
            </div>
        );
    });

    // Fetch employees
    const fetchEmployees = async () => {
        try {
            updateLoadingState('employees', true);
            const response = await fetch(`${API_BASE_URL}/recurring-expenses/employees`, {
                headers: getAuthHeaders()
            });
            const data = await response.json();
            if (data.success) {
                setEmployees(data.employees || []);
            }
        } catch (error) {
            handleApiError(error, 'employees');
            setEmployees([]);
        } finally {
            updateLoadingState('employees', false);
        }
    };

    // Fetch projects
    const fetchProjects = async () => {
        try {
            updateLoadingState('projects', true);
            const response = await fetch(`${API_BASE_URL}/recurring-expenses/projects`, {
                headers: getAuthHeaders()
            });
            const data = await response.json();
            if (data.success) {
                setProjects(data.projects || []);
            }
        } catch (error) {
            handleApiError(error, 'projects');
            setProjects([]);
        } finally {
            updateLoadingState('projects', false);
        }
    };

    // Fetch customers
    const fetchCustomers = async () => {
        try {
            updateLoadingState('customers', true);
            const response = await fetch(`${API_BASE_URL}/recurring-expenses/customers`, {
                headers: getAuthHeaders()
            });
            const data = await response.json();
            if (data.success) {
                setCustomers(data.customers || []);
            }
        } catch (error) {
            handleApiError(error, 'customers');
            setCustomers([]);
        } finally {
            updateLoadingState('customers', false);
        }
    };

    // Fetch taxes
    const fetchTaxes = async () => {
        try {
            updateLoadingState('taxes', true);
            const response = await fetch(`${API_BASE_URL}/recurring-expenses/taxes`, {
                headers: getAuthHeaders()
            });
            const data = await response.json();
            if (data.success) {
                setTaxes(data.taxes || []);
            }
        } catch (error) {
            handleApiError(error, 'taxes');
            setTaxes([]);
        } finally {
            updateLoadingState('taxes', false);
        }
    };

    // Validate recurring expense
    const validateRecurringExpense = (expense) => {
        const missingFields = [];
        const warnings = [];

        if (!expense.recurrence_name) missingFields.push('Recurrence Name');
        if (!expense.description) missingFields.push('Description');
        if (!expense.account_id) missingFields.push('Expense Account');
        if (!expense.paid_through_account_id) missingFields.push('Payment Account');
        if (!expense.amount || expense.amount <= 0) missingFields.push('Amount');
        if (!expense.start_date) missingFields.push('Start Date');
        if (!expense.recurrence_frequency) missingFields.push('Frequency');

        if (expense.start_date && expense.end_date) {
            const start = new Date(expense.start_date);
            const end = new Date(expense.end_date);
            if (start >= end) {
                warnings.push('End date should be after start date');
            }
        }

        if (expense.is_billable && !expense.customer_id) {
            warnings.push('Billable expenses should have a customer assigned');
        }

        if (expense.is_reimbursable && !expense.employee_id) {
            warnings.push('Reimbursable expenses should have an employee assigned');
        }

        if (expense.amount && expense.amount > 50000) {
            warnings.push('High expense amount - may require approval');
        }

        const completionPercentage = Math.round(((7 - missingFields.length) / 7) * 100);

        return {
            isValid: missingFields.length === 0,
            missingFields,
            warnings,
            completionPercentage,
            canSync: missingFields.length === 0
        };
    };

    // Get AI suggestions
    const getAISuggestions = async (description, category = 'general') => {
        try {
            const response = await fetch(`${API_BASE_URL}/recurring-expenses/ai/suggest-recurring-expense-data`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ description, category })
            });
            const data = await response.json();
            return data.suggestions || {};
        } catch (error) {
            console.error('AI suggestions error:', error);
            return {};
        }
    };

    // FIXED: Enhanced payment account validation
    const validatePaymentAccount = useCallback((accountId) => {
        if (!accountId) return { isValid: false, message: 'Please select a payment account' };

        const account = paymentAccounts.find(acc => acc.account_id === accountId);
        if (!account) {
            return {
                isValid: false,
                message: 'Selected payment account not found in Zoho Books. Please refresh accounts or select another.'
            };
        }

        return { isValid: true, account };
    }, [paymentAccounts]);

    // FIXED: Enhanced expense account validation
    const validateExpenseAccount = useCallback((accountId) => {
        if (!accountId) return { isValid: false, message: 'Please select an expense account' };

        const account = expenseAccounts.find(acc => acc.account_id === accountId);
        if (!account) {
            return {
                isValid: false,
                message: 'Selected expense account not found in Zoho Books. Please refresh accounts or select another.'
            };
        }

        return { isValid: true, account };
    }, [expenseAccounts]);

    // FIXED: Sync recurring expenses with approval check and correct field names
    const syncRecurringExpenses = async (expenseIds = null) => {
        try {
            setLoading(true);
            setSyncProgress({ current: 0, total: 0, status: 'Preparing sync...' });

            const expensesToSync = expenseIds || selectedExpenses;
            if (expensesToSync.length === 0) {
                alert('Please select recurring expenses to sync');
                return;
            }

            // Check approval status for each expense
            for (const id of expensesToSync) {
                const expense = recurringExpenses.find(exp => exp.id === id);
                if (!canSync(expense)) {
                    const status = expense.approver_status || expense.approvalInfo?.status;
                    let message = 'Cannot sync expense - ';
                    if (status === 'pending_approval') {
                        message += 'pending approval';
                    } else if (status === 'rejected') {
                        message += 'rejected';
                    } else {
                        message += 'not approved';
                    }
                    alert(message);
                    return;
                }
            }

            const syncData = expensesToSync.map(id => {
                const expense = recurringExpenses.find(exp => exp.id === id);
                const validation = validateRecurringExpense(expense);

                if (!validation.canSync) {
                    throw new Error(`Expense ${expense.recurrence_name} is not ready for sync: ${validation.missingFields.join(', ')}`);
                }

                return {
                    recurring_expense_id: expense.id,
                    recurrence_name: expense.recurrence_name,
                    account_id: expense.account_id,
                    paid_through_account_id: expense.paid_through_account_id,
                    description: expense.description,
                    amount: parseFloat(expense.amount),
                    start_date: expense.start_date,
                    end_date: expense.end_date,
                    recurrence_frequency: expense.recurrence_frequency,
                    repeat_every: expense.repeat_every || 1,
                    currency_code: expense.currency_code || 'INR',
                    is_billable: expense.is_billable || false,
                    is_reimbursable: expense.is_reimbursable || false,
                    employee_id: expense.employee_id || '',
                    project_id: expense.project_id || '',
                    customer_id: expense.customer_id || '',
                    notes: expense.notes || ''
                };
            });

            setSyncProgress({ current: 0, total: syncData.length, status: 'Starting sync...' });

            const response = await fetch(`${API_BASE_URL}/recurring-expenses/sync-to-zoho`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ recurringExpenseUpdates: syncData })
            });

            const result = await response.json();

            if (result.success) {
                console.log('Recurring expenses sync completed:', result.summary);
                await fetchRecurringExpenses();
                setSelectedExpenses([]);
                alert(`Sync completed! ${result.summary.successful}/${result.summary.total} (${result.summary.success_rate}% success rate)`);
            } else {
                throw new Error(result.message || 'Sync failed');
            }
        } catch (error) {
            console.error('Sync error:', error);
            alert(`Sync failed: ${error.message}`);
        } finally {
            setLoading(false);
            setSyncProgress(null);
        }
    };

    // Create new recurring expense with validation
    const createRecurringExpense = useCallback(async (expenseData) => {
        try {
            setLoading(true);

            // Validate payment account
            const paymentValidation = validatePaymentAccount(expenseData.paid_through_account_id);
            if (!paymentValidation.isValid) {
                alert(paymentValidation.message);
                return;
            }

            // Validate expense account
            const expenseValidation = validateExpenseAccount(expenseData.account_id);
            if (!expenseValidation.isValid) {
                alert(expenseValidation.message);
                return;
            }

            // Validate required fields
            const requiredFields = ['recurrence_name', 'description', 'account_id', 'paid_through_account_id', 'amount', 'start_date'];
            const missingFields = requiredFields.filter(field => !expenseData[field]);

            if (missingFields.length > 0) {
                throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
            }

            // FIXED: Proper data formatting with frequency mapping
            const formattedData = {
                ...expenseData,
                amount: parseFloat(expenseData.amount),
                repeat_every: expenseData.recurrence_frequency === 'quarterly' ? 3 : parseInt(expenseData.repeat_every) || 1,
                recurrence_frequency: mapFormFrequencyToDb(expenseData.recurrence_frequency),
                tags: Array.isArray(expenseData.tags) ? expenseData.tags : [],
                company_id: currentOrganization?.organization_id || 1
            };

            console.log('Creating expense with formatted data:', formattedData);

            const response = await fetch(`${API_BASE_URL}/recurring-expenses`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(formattedData)
            });

            const data = await response.json();

            if (data.success) {
                await fetchRecurringExpenses();
                setShowCreateModal(false);
                resetForm();
                alert('Recurring expense created successfully!');
            } else {
                throw new Error(data.message || 'Failed to create expense');
            }
        } catch (error) {
            console.error('❌ Create error:', error);
            alert(`Failed to create expense: ${error.message}`);
        } finally {
            setLoading(false);
        }
    }, [validatePaymentAccount, validateExpenseAccount, mapFormFrequencyToDb, currentOrganization, getAuthHeaders, fetchRecurringExpenses, resetForm]);

    // Update existing recurring expense with validation
    const updateRecurringExpense = useCallback(async (id, expenseData) => {
        try {
            setLoading(true);

            // Validate accounts
            const paymentValidation = validatePaymentAccount(expenseData.paid_through_account_id);
            if (!paymentValidation.isValid) {
                alert(paymentValidation.message);
                return;
            }

            const expenseValidation = validateExpenseAccount(expenseData.account_id);
            if (!expenseValidation.isValid) {
                alert(expenseValidation.message);
                return;
            }

            // FIXED: Proper data formatting with frequency mapping
            const formattedData = {
                ...expenseData,
                amount: parseFloat(expenseData.amount),
                repeat_every: expenseData.recurrence_frequency === 'quarterly' ? 3 : parseInt(expenseData.repeat_every) || 1,
                recurrence_frequency: mapFormFrequencyToDb(expenseData.recurrence_frequency),
                tags: Array.isArray(expenseData.tags) ? expenseData.tags : [],
                company_id: currentOrganization?.organization_id
            };

            console.log('Updating expense with formatted data:', formattedData);

            const response = await fetch(`${API_BASE_URL}/recurring-expenses/${id}`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify(formattedData)
            });

            const data = await response.json();

            if (data.success) {
                setRecurringExpenses(prev =>
                    prev.map(exp => exp.id === data.data.id ? data.data : exp)
                );
                setShowEditModal(false);
                alert('Recurring expense updated successfully!');
            } else {
                throw new Error(data.message || 'Failed to update expense');
            }
        } catch (error) {
            console.error('❌ Update error:', error);
            alert(`Failed to update expense: ${error.message}`);
        } finally {
            setLoading(false);
        }
    }, [validatePaymentAccount, validateExpenseAccount, mapFormFrequencyToDb, currentOrganization, getAuthHeaders]);

    // Delete recurring expense
    const deleteRecurringExpense = async (id) => {
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE_URL}/recurring-expenses/${id}`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });

            const data = await response.json();

            if (data.success) {
                setRecurringExpenses(prev => prev.filter(exp => exp.id !== id));
                setShowDeleteModal(false);
                alert('Recurring expense deleted successfully!');
            } else {
                throw new Error(data.message || 'Failed to delete expense');
            }
        } catch (error) {
            console.error('❌ Delete error:', error);
            alert(`Failed to delete expense: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Load all data on component mount
    useEffect(() => {
        const loadAllData = async () => {
            console.log('🚀 Loading all data...');
            if (currentOrganization?.organization_id) {
                await Promise.all([
                    fetchRecurringExpenses(),
                    fetchExpenseAccounts(),
                    fetchPaymentAccounts(),
                    fetchEmployees(),
                    fetchProjects(),
                    fetchCustomers(),
                    fetchTaxes()
                ]);
            }
            console.log('✅ All data loaded');
        };

        loadAllData();
    }, [currentOrganization?.organization_id]);

    // Clean up selection when expenses are synced
    useEffect(() => {
        const syncedExpenseIds = recurringExpenses
            .filter(exp => exp.zoho_recurring_expense_id)
            .map(exp => exp.id);

        if (syncedExpenseIds.length > 0) {
            setSelectedExpenses(prev => prev.filter(id => !syncedExpenseIds.includes(id)));
        }
    }, [recurringExpenses]);

    // Filter expenses based on search and filter criteria
    const filteredExpenses = recurringExpenses.filter(expense => {
        const matchesSearch = expense.recurrence_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            expense.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            expense.category?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesFilter = filterStatus === 'all' ||
            (filterStatus === 'active' && expense.status === 'active') ||
            (filterStatus === 'inactive' && expense.status === 'inactive') ||
            (filterStatus === 'synced' && expense.zoho_recurring_expense_id) ||
            (filterStatus === 'pending' && !expense.zoho_recurring_expense_id) ||
            (filterStatus === 'billable' && expense.is_billable) ||
            (filterStatus === 'reimbursable' && expense.is_reimbursable);

        return matchesSearch && matchesFilter;
    });

    // FIXED: Handle edit with proper form initialization
    const handleEdit = useCallback((expense) => {
        console.log('Editing expense:', expense);

        // Check if expense can be edited
        if (!canEdit(expense)) {
            const status = expense.approver_status || expense.approvalInfo?.status;
            let message = 'This expense cannot be edited - ';
            if (status === 'pending_approval') {
                message += 'pending approval';
            } else if (status === 'rejected') {
                message += 'rejected';
            } else {
                message += 'not approved';
            }
            alert(message);
            return;
        }

        if (expense.zoho_recurring_expense_id) {
            alert('This expense has already been synced to Zoho Books and cannot be edited to maintain data consistency.');
            return;
        }

        setCurrentExpense(expense);

        // Parse tags properly
        let parsedTags = [];
        try {
            if (typeof expense.tags === 'string') {
                parsedTags = expense.tags ? JSON.parse(expense.tags) : [];
            } else if (Array.isArray(expense.tags)) {
                parsedTags = expense.tags;
            }
        } catch (error) {
            console.error('Error parsing tags:', error);
            parsedTags = [];
        }

        // FIXED: Single batch update to prevent multiple re-renders
        const formData = {
            recurrence_name: expense.recurrence_name || '',
            expense_type: expense.expense_type || 'business',
            category: expense.category || 'general',
            description: expense.description || '',
            account_id: expense.account_id || '',
            account_name: expense.account_name || '',
            paid_through_account_id: expense.paid_through_account_id || '',
            paid_through_account_name: expense.paid_through_account_name || '',
            amount: expense.amount ? String(expense.amount) : '',
            currency_code: expense.currency_code || 'INR',
            start_date: formatDateForInput(expense.start_date),
            end_date: formatDateForInput(expense.end_date),
            recurrence_frequency: mapDbFrequencyToForm(expense.recurrence_frequency, expense.repeat_every),
            repeat_every: expense.repeat_every || 1,
            is_billable: Boolean(expense.is_billable),
            is_reimbursable: Boolean(expense.is_reimbursable),
            receipt_required: Boolean(expense.receipt_required),
            auto_submit: Boolean(expense.auto_submit),
            employee_id: expense.employee_id || '',
            employee_name: expense.employee_name || '',
            project_id: expense.project_id || '',
            project_name: expense.project_name || '',
            customer_id: expense.customer_id || '',
            customer_name: expense.customer_name || '',
            notes: expense.notes || '',
            tags: parsedTags
        };

        // Single state update to set all form data at once
        setRecurringExpenseForm(formData);
        setShowEditModal(true);
    }, [formatDateForInput, mapDbFrequencyToForm, canEdit]);

    const handleDelete = (expense) => {
        setCurrentExpense(expense);
        setShowDeleteModal(true);
    };

    const handleSync = () => {
        setShowSyncModal(true);
    };

    const handleValidate = (expense) => {
        const validation = validateRecurringExpense(expense);
        setValidationResults(validation);
        setCurrentExpense(expense);
        setShowValidationModal(true);
    };

    // Loading indicator component
    const LoadingIndicator = ({ text = 'Loading...' }) => (
        <div className="flex items-center space-x-2">
            <Loader className="w-4 h-4 animate-spin" />
            <span className="text-sm text-gray-600">{text}</span>
        </div>
    );

    // Error display component
    const ErrorDisplay = ({ error, onRetry, context }) => (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-center space-x-2">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                <span className="text-sm text-red-800">{error}</span>
            </div>
            {onRetry && (
                <button
                    onClick={onRetry}
                    className="mt-2 text-xs text-red-600 underline hover:text-red-800"
                >
                    Retry
                </button>
            )}
        </div>
    );

    // Enhanced dropdown component for payment accounts
    const PaymentAccountDropdown = React.memo(() => {
        const getAccountTypeLabel = (accountType) => {
            const typeLabels = {
                'bank': '🏦 Bank',
                'cash': '💵 Cash',
                'credit_card': '💳 Credit Card',
                'other_current_asset': '💰 Digital Wallet'
            };
            return typeLabels[accountType?.toLowerCase()] || '📊 Other';
        };

        const selectedAccount = paymentAccounts.find(acc => acc.account_id === recurringExpenseForm.paid_through_account_id);
        const accountValidation = validatePaymentAccount(recurringExpenseForm.paid_through_account_id);

        return (
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Paid Through Account *
                    {loadingStates.paymentAccounts && <span className="text-blue-500 ml-2">(Loading...)</span>}
                    {paymentAccounts.length > 0 && (
                        <span className="text-green-500 ml-2">({paymentAccounts.length} accounts available)</span>
                    )}
                </label>
                <select
                    value={recurringExpenseForm.paid_through_account_id || ''}
                    onChange={(e) => {
                        const account = paymentAccounts.find(acc => acc.account_id === e.target.value);
                        updateFormField('paid_through_account_id', e.target.value);
                        updateFormField('paid_through_account_name', account?.account_name || '');
                    }}
                    disabled={loadingStates.paymentAccounts}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 ${recurringExpenseForm.paid_through_account_id && !accountValidation.isValid
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                        }`}
                    required
                >
                    <option value="">
                        {loadingStates.paymentAccounts ? 'Loading payment accounts...' :
                            paymentAccounts.length === 0 ? 'No payment accounts available' :
                                'Select Payment Account'}
                    </option>
                    {paymentAccounts.map(account => (
                        <option key={`payment-${account.account_id}`} value={account.account_id}>
                            {getAccountTypeLabel(account.account_type)} {account.account_name}
                            {account.account_code && ` (${account.account_code})`}
                            {account.balance && ` - ₹${account.balance.toLocaleString()}`}
                        </option>
                    ))}
                </select>

                {/* Account validation message */}
                {recurringExpenseForm.paid_through_account_id && !accountValidation.isValid && (
                    <div className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertTriangle className="w-4 h-4 mr-1" />
                        {accountValidation.message}
                    </div>
                )}

                {/* Selected account details */}
                {selectedAccount && accountValidation.isValid && (
                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm">
                        <span className="font-medium text-green-800">✓ Account verified: </span>
                        <span className="text-green-700">
                            {getAccountTypeLabel(selectedAccount.account_type)} {selectedAccount.account_name}
                        </span>
                    </div>
                )}
            </div>
        );
    });

    // Dropdown component for expense accounts
    const ExpenseAccountDropdown = React.memo(() => {
        const selectedAccount = expenseAccounts.find(acc => acc.account_id === recurringExpenseForm.account_id);
        const accountValidation = validateExpenseAccount(recurringExpenseForm.account_id);

        return (
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expense Account *
                    {loadingStates.expenseAccounts && <span className="text-blue-500 ml-2">(Loading...)</span>}
                </label>
                <select
                    value={recurringExpenseForm.account_id || ''}
                    onChange={(e) => {
                        const account = expenseAccounts.find(acc => acc.account_id === e.target.value);
                        updateFormField('account_id', e.target.value);
                        updateFormField('account_name', account?.account_name || '');
                    }}
                    disabled={loadingStates.expenseAccounts}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 ${recurringExpenseForm.account_id && !accountValidation.isValid
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                        }`}
                    required
                >
                    <option value="">
                        {loadingStates.expenseAccounts ? 'Loading...' :
                            expenseAccounts.length === 0 ? 'No expense accounts available' :
                                'Select Expense Account'}
                    </option>
                    {expenseAccounts.map(account => (
                        <option key={`expense-${account.account_id}`} value={account.account_id}>
                            {account.account_name} {account.account_code && `(${account.account_code})`}
                        </option>
                    ))}
                </select>

                {/* Account validation message */}
                {recurringExpenseForm.account_id && !accountValidation.isValid && (
                    <div className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertTriangle className="w-4 h-4 mr-1" />
                        {accountValidation.message}
                    </div>
                )}

                {/* Selected account details */}
                {selectedAccount && accountValidation.isValid && (
                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm">
                        <span className="font-medium text-green-800">✓ Account verified: </span>
                        <span className="text-green-700">{selectedAccount.account_name}</span>
                    </div>
                )}

                {/* AI Suggestion */}
                {recurringExpenseForm.recurrence_name && !recurringExpenseForm.account_id && (
                    <div className="mt-2">
                        <button
                            type="button"
                            onClick={() => {
                                const suggestions = getAISuggestionsForExpense(recurringExpenseForm.recurrence_name);
                                if (suggestions.account_id) {
                                    updateFormField('account_id', suggestions.account_id);
                                    updateFormField('account_name', suggestions.account_name);
                                }
                            }}
                            className="text-sm text-purple-600 hover:text-purple-800 underline"
                        >
                            🤖 Get AI suggestion for expense account
                        </button>
                    </div>
                )}
            </div>
        );
    });

    // Overview Dashboard Component
    const OverviewDashboard = () => (
        <div className="space-y-6">
            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white rounded-lg border p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total Recurring Expenses</p>
                            <p className="text-2xl font-bold text-gray-900">{statistics.total || 0}</p>
                        </div>
                        <Receipt className="w-8 h-8 text-purple-600" />
                    </div>
                    <div className="mt-4 flex items-center">
                        <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
                        <span className="text-sm text-green-600">Ready to sync: {statistics.ready || 0}</span>
                    </div>
                </div>

                <div className="bg-white rounded-lg border p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Active Expenses</p>
                            <p className="text-2xl font-bold text-green-600">{statistics.active || 0}</p>
                        </div>
                        <PlayCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <div className="mt-4 flex items-center">
                        <Clock className="w-4 h-4 text-blue-500 mr-1" />
                        <span className="text-sm text-blue-600">Monthly: {statistics.monthly || 0}</span>
                    </div>
                </div>

                <div className="bg-white rounded-lg border p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Synced to Zoho</p>
                            <p className="text-2xl font-bold text-teal-600">{statistics.synced || 0}</p>
                        </div>
                        <CheckCircle className="w-8 h-8 text-teal-600" />
                    </div>
                    <div className="mt-4 flex items-center">
                        <Target className="w-4 h-4 text-teal-500 mr-1" />
                        <span className="text-sm text-teal-600">Success rate: {statistics.success_rate || 0}%</span>
                    </div>
                </div>
            </div>

            {/* Quick Actions */}
            {/* <div className="bg-white rounded-lg border p-6">
                <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <button
                        onClick={() => {
                            resetForm();
                            setShowCreateModal(true);
                        }}
                        className="flex items-center justify-center gap-2 p-4 border border-purple-200 rounded-lg hover:bg-purple-50 transition-colors"
                    >
                        <Plus className="w-5 h-5 text-purple-600" />
                        <span className="text-purple-700 font-medium">Create New Expense</span>
                    </button>

                    <button
                        onClick={() => setActiveTab('manage')}
                        className="flex items-center justify-center gap-2 p-4 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
                    >
                        <Settings className="w-5 h-5 text-blue-600" />
                        <span className="text-blue-700 font-medium">Manage Expenses</span>
                    </button>

                    <button
                        onClick={() => {
                            const readyExpenses = recurringExpenses.filter(exp => {
                                const validation = validateRecurringExpense(exp);
                                return validation.isValid && !exp.zoho_recurring_expense_id;
                            });
                            if (readyExpenses.length === 0) {
                                alert('No expenses are ready for sync');
                                return;
                            }
                            setSelectedExpenses(readyExpenses.map(exp => exp.id));
                            handleSync();
                        }}
                        className="flex items-center justify-center gap-2 p-4 border border-green-200 rounded-lg hover:bg-green-50 transition-colors"
                    >
                        <Zap className="w-5 h-5 text-green-600" />
                        <span className="text-green-700 font-medium">Sync Ready Expenses</span>
                    </button>

                    <button
                        onClick={() => {
                            fetchRecurringExpenses();
                            fetchExpenseAccounts();
                            fetchPaymentAccounts();
                        }}
                        disabled={loading}
                        className="flex items-center justify-center gap-2 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                        <RefreshCw className={`w-5 h-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
                        <span className="text-gray-700 font-medium">Refresh Data</span>
                    </button>
                </div>
            </div> */}

            {/* Recent Expenses */}
            <div className="bg-white rounded-lg border p-6">
                <h3 className="text-lg font-semibold mb-4">Recent Recurring Expenses</h3>
                {loadingStates.expenses ? (
                    <LoadingIndicator text="Loading expenses..." />
                ) : errors.expenses ? (
                    <ErrorDisplay error={errors.expenses} onRetry={fetchRecurringExpenses} />
                ) : (
                    <div className="space-y-3">
                        {filteredExpenses.slice(0, 5).map(expense => {
                            const validation = validateRecurringExpense(expense);
                            const category = expenseCategories.find(cat => cat.id === expense.category);
                            const CategoryIcon = category?.icon || Receipt;

                            return (
                                <div key={expense.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                                    <div className="flex items-center space-x-3">
                                        <div className={`w-3 h-3 rounded-full ${expense.zoho_recurring_expense_id ? 'bg-green-500' :
                                            validation.isValid ? 'bg-yellow-500' : 'bg-red-500'
                                            }`} />
                                        <CategoryIcon className={`w-5 h-5 text-${category?.color || 'gray'}-600`} />
                                        <div>
                                            <p className="font-medium">{expense.recurrence_name}</p>
                                            <p className="text-sm text-gray-500">
                                                {expense.recurrence_frequency} • ₹{expense.amount?.toLocaleString()}
                                                {expense.is_billable && <span className="text-blue-600 ml-1">• Billable</span>}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        {getApprovalStatusDisplay(expense)}
                                        <div className="text-right">
                                            <p className="text-sm font-medium">₹{expense.amount?.toLocaleString()}</p>
                                            <p className="text-xs text-gray-500">{expense.recurrence_frequency}</p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        {filteredExpenses.length === 0 && (
                            <p className="text-gray-500 text-center py-8">No recurring expenses found</p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );

    // FIXED: Manage Expenses Component with proper action buttons
    const ManageExpenses = () => {
        const renderActionButtons = (expense) => {
            const isSynced = !!expense.zoho_recurring_expense_id;
            const canEditExpense = canEdit(expense);
            const canSyncExpense = canSync(expense);

            return (
                <div className="flex items-center space-x-2">
                    <button
                        onClick={() => handleValidate(expense)}
                        disabled={isSynced}
                        className={isSynced ? "text-gray-400 cursor-not-allowed" : "text-green-600 hover:text-green-900"}
                        title={isSynced ? "Cannot validate synced expense" : "Validate"}
                    >
                        <CheckCircle className="w-4 h-4" />
                    </button>

                    {(!isSynced) && (
                        <button
                            onClick={() => handleEdit(expense)}
                            disabled={!canEditExpense}
                            className={canEditExpense ? "text-purple-600 hover:text-purple-800" : "text-gray-400 cursor-not-allowed"}
                            title={!canEditExpense ? "Cannot edit - approval required" : "Edit Expense"}
                        >
                            <Edit className="w-4 h-4" />
                        </button>
                    )}

                    {isSynced && (
                        <button disabled className="text-gray-400 cursor-not-allowed" title="Cannot edit synced expense">
                            <Edit className="w-4 h-4" />
                        </button>
                    )}

                    {!isSynced && (
                        <button
                            onClick={() => {
                                setSyncing(expense.id);
                                syncRecurringExpenses([expense.id]).finally(() => setSyncing(null));
                            }}
                            disabled={syncing === expense.id || !canSyncExpense}
                            className={canSyncExpense ? "text-green-600 hover:text-green-800" : "text-gray-400 cursor-not-allowed"}
                            title={!canSyncExpense ? "Cannot sync - approval required" : "Sync to Zoho"}
                        >
                            <RefreshCw className={`w-4 h-4 ${syncing === expense.id ? 'animate-spin' : ''}`} />
                        </button>
                    )}

                    {isSynced && (
                        <button disabled className="text-gray-400 cursor-not-allowed" title="Already synced to Zoho Books">
                            <RefreshCw className="w-4 h-4" />
                        </button>
                    )}

                    {/* <button
                        onClick={() => handleDelete(expense)}
                        disabled={isSynced}
                        className={isSynced ? "text-gray-400 cursor-not-allowed" : "text-red-600 hover:text-red-800"}
                        title={isSynced ? "Cannot delete synced expense" : "Delete"}
                    >
                        <Trash2 className="w-4 h-4" />
                    </button> */}

                    {/* <button
                        onClick={() => {
                            setCurrentExpense(expense);
                        }}
                        className="text-blue-600 hover:text-blue-800"
                        title="View Details"
                    >
                        <Eye className="w-4 h-4" />
                    </button> */}
                </div>
            );
        };

        return (
            <div className="space-y-6">
                {/* Header with Actions */}
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">Manage Recurring Expenses</h2>
                        <p className="text-gray-600">Create, edit, validate, and sync recurring expenses</p>
                    </div>

                    <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                        {/* <button
                            onClick={() => {
                                resetForm();
                                setShowCreateModal(true);
                            }}
                            className="flex items-center justify-center space-x-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                        >
                            <Plus className="w-4 h-4" />
                            <span>Create New</span>
                        </button> */}

                        <button
                            onClick={() => {
                                if (selectedExpenses.length === 0) {
                                    alert('Please select expenses to sync');
                                    return;
                                }
                                handleSync();
                            }}
                            disabled={selectedExpenses.length === 0 || loading}
                            className="flex items-center justify-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400"
                        >
                            <RefreshCw className="w-4 h-4" />
                            <span>Sync Selected ({selectedExpenses.length})</span>
                        </button>

                        <button
                            onClick={() => {
                                fetchRecurringExpenses();
                                fetchExpenseAccounts();
                                fetchPaymentAccounts();
                            }}
                            disabled={loading}
                            className="flex items-center justify-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors disabled:bg-gray-400"
                        >
                            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                            <span>Refresh</span>
                        </button>
                    </div>
                </div>

                {/* Search and Filter */}
                <div className="flex flex-col lg:flex-row space-y-4 lg:space-y-0 lg:space-x-4">
                    <div className="flex-1">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                                type="text"
                                placeholder="Search by name, description, or category..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            />
                        </div>
                    </div>

                    <div className="flex space-x-2">
                        <div className="relative">
                            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <select
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                                className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none bg-white"
                            >
                                <option value="all">All Status</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                                <option value="synced">Synced</option>
                                <option value="pending">Pending Sync</option>
                                <option value="billable">Billable</option>
                                <option value="reimbursable">Reimbursable</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Results Summary */}
                <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <p className="text-sm text-gray-600">
                                Showing {filteredExpenses.length} of {recurringExpenses.length} expenses
                            </p>
                            <p className="text-sm text-gray-500">
                                {filteredExpenses.filter(exp => !exp.zoho_recurring_expense_id).length} selectable
                            </p>
                            {selectedExpenses.length > 0 && (
                                <p className="text-sm text-purple-600 font-medium">
                                    {selectedExpenses.length} selected
                                </p>
                            )}
                        </div>

                        <div className="flex items-center space-x-2 text-sm">
                            <div className="flex items-center space-x-1">
                                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                <span className="text-gray-600">Synced</span>
                            </div>
                            <div className="flex items-center space-x-1">
                                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                                <span className="text-gray-600">Ready</span>
                            </div>
                            <div className="flex items-center space-x-1">
                                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                                <span className="text-gray-600">Incomplete</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Expenses Table */}
                {loadingStates.expenses ? (
                    <div className="bg-white rounded-lg border p-8">
                        <LoadingIndicator text="Loading recurring expenses..." />
                    </div>
                ) : errors.expenses ? (
                    <div className="bg-white rounded-lg border p-8">
                        <ErrorDisplay error={errors.expenses} onRetry={fetchRecurringExpenses} />
                    </div>
                ) : (
                    <div className="bg-white rounded-lg border overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            <input
                                                type="checkbox"
                                                checked={
                                                    selectedExpenses.length > 0 &&
                                                    filteredExpenses.filter(exp => !exp.zoho_recurring_expense_id).every(exp => selectedExpenses.includes(exp.id)) &&
                                                    filteredExpenses.filter(exp => !exp.zoho_recurring_expense_id).length > 0
                                                }
                                                onChange={(e) => {
                                                    const selectableExpenses = filteredExpenses.filter(exp => !exp.zoho_recurring_expense_id);
                                                    if (e.target.checked) {
                                                        setSelectedExpenses([
                                                            ...selectedExpenses.filter(id => !selectableExpenses.map(exp => exp.id).includes(id)),
                                                            ...selectableExpenses.map(exp => exp.id)
                                                        ]);
                                                    } else {
                                                        const selectableIds = selectableExpenses.map(exp => exp.id);
                                                        setSelectedExpenses(selectedExpenses.filter(id => !selectableIds.includes(id)));
                                                    }
                                                }}
                                                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                                title="Select all non-synced expenses"
                                            />
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Expense Details
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Approval Status
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Amount & Frequency
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Accounts
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Validation
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredExpenses.map((expense) => {
                                        const validation = validateRecurringExpense(expense);
                                        const category = expenseCategories.find(cat => cat.id === expense.category);
                                        const CategoryIcon = category?.icon || Receipt;
                                        const isSynced = !!expense.zoho_recurring_expense_id;

                                        return (
                                            <tr key={expense.id} className={`hover:bg-gray-50 ${selectedExpenses.includes(expense.id) ? 'bg-purple-50' : ''}`}>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedExpenses.includes(expense.id)}
                                                        disabled={!!expense.zoho_recurring_expense_id}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setSelectedExpenses([...selectedExpenses, expense.id]);
                                                            } else {
                                                                setSelectedExpenses(selectedExpenses.filter(id => id !== expense.id));
                                                            }
                                                        }}
                                                        className={`rounded border-gray-300 text-purple-600 focus:ring-purple-500 ${!!expense.zoho_recurring_expense_id ? 'opacity-50 cursor-not-allowed' : ''
                                                            }`}
                                                        title={!!expense.zoho_recurring_expense_id ? 'Already synced to Zoho Books' : ''}
                                                    />
                                                </td>

                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        <div className={`w-3 h-3 rounded-full mr-2 ${isSynced ? 'bg-green-500' :
                                                            validation.isValid ? 'bg-yellow-500' : 'bg-red-500'
                                                            }`} />
                                                        <span className="text-sm font-medium text-gray-900">
                                                            {isSynced ? 'Synced' : validation.isValid ? 'Ready' : 'Incomplete'}
                                                        </span>
                                                    </div>
                                                    {isSynced && (
                                                        <div className="text-xs text-green-600 mt-1">
                                                            ID: {expense.zoho_recurring_expense_id}
                                                        </div>
                                                    )}
                                                </td>

                                                <td className="px-6 py-4">
                                                    <div className="flex items-center">
                                                        <CategoryIcon className={`w-5 h-5 text-${category?.color || 'gray'}-600 mr-3`} />
                                                        <div>
                                                            <div className="text-sm font-medium text-gray-900">
                                                                {expense.recurrence_name}
                                                            </div>
                                                            <div className="text-sm text-gray-500">
                                                                {expense.description}
                                                            </div>
                                                            <div className="flex items-center space-x-2 mt-1">
                                                                <span className={`px-2 py-1 text-xs rounded-full bg-${category?.color || 'gray'}-100 text-${category?.color || 'gray'}-800`}>
                                                                    {category?.name || expense.category}
                                                                </span>
                                                                {expense.is_billable && (
                                                                    <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                                                                        Billable
                                                                    </span>
                                                                )}
                                                                {expense.is_reimbursable && (
                                                                    <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-800">
                                                                        Reimbursable
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>

                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {getApprovalStatusDisplay(expense)}
                                                </td>

                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        ₹{expense.amount?.toLocaleString()}
                                                    </div>
                                                    <div className="text-sm text-gray-500 capitalize">
                                                        {expense.recurrence_frequency}
                                                        {expense.repeat_every > 1 && ` (every ${expense.repeat_every})`}
                                                    </div>
                                                    <div className="text-xs text-gray-400 mt-1">
                                                        {expense.start_date && `From: ${new Date(expense.start_date).toLocaleDateString()}`}
                                                    </div>
                                                </td>

                                                <td className="px-6 py-4">
                                                    <div className="text-sm text-gray-900">
                                                        <div className="font-medium mb-1">Expense:</div>
                                                        <div className="text-xs text-gray-600 mb-2">
                                                            {expense.account_name || 'Not selected'}
                                                        </div>
                                                        <div className="font-medium mb-1">Payment:</div>
                                                        <div className="text-xs text-gray-600">
                                                            {expense.paid_through_account_name || 'Not selected'}
                                                        </div>
                                                    </div>
                                                </td>

                                                <td className="px-6 py-4">
                                                    <div className="flex items-center">
                                                        {validation.isValid ? (
                                                            <div className="flex items-center text-green-600">
                                                                <CheckCircle className="w-4 h-4 mr-1" />
                                                                <span className="text-xs">Valid</span>
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center text-red-600">
                                                                <XCircle className="w-4 h-4 mr-1" />
                                                                <span className="text-xs">{validation.missingFields.length} missing</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                                                        <div
                                                            className={`h-1.5 rounded-full ${validation.completionPercentage === 100 ? 'bg-green-500' :
                                                                validation.completionPercentage >= 75 ? 'bg-yellow-500' : 'bg-red-500'
                                                                }`}
                                                            style={{ width: `${validation.completionPercentage}%` }}
                                                        />
                                                    </div>
                                                    <div className="text-xs text-gray-500 mt-1">
                                                        {validation.completionPercentage}% complete
                                                    </div>
                                                </td>

                                                <td className="px-6 py-4 whitespace-nowrap text-right">
                                                    {renderActionButtons(expense)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {filteredExpenses.length === 0 && (
                            <div className="text-center py-12">
                                <FolderOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-gray-900 mb-2">No expenses found</h3>
                                <p className="text-gray-500 mb-4">
                                    {searchTerm || filterStatus !== 'all'
                                        ? 'Try adjusting your search or filter criteria'
                                        : 'Get started by creating your first recurring expense'
                                    }
                                </p>
                                {!searchTerm && filterStatus === 'all' && (
                                    <button
                                        onClick={() => {
                                            resetForm();
                                            setShowCreateModal(true);
                                        }}
                                        className="inline-flex items-center space-x-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                                    >
                                        <Plus className="w-4 h-4" />
                                        <span>Create First Expense</span>
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // FIXED: Create/Edit Modal with stable form inputs and proper date handling
    const CreateEditModal = () => {
        const isEditing = !!currentExpense;
        const modalTitle = isEditing ? 'Edit Recurring Expense' : 'Create New Recurring Expense';

        const handleSubmit = async (e) => {
            e.preventDefault();
            const formData = { ...recurringExpenseForm };

            if (isEditing) {
                await updateRecurringExpense(currentExpense.id, formData);
            } else {
                await createRecurringExpense(formData);
            }
        };

        const handleClose = () => {
            setShowCreateModal(false);
            setShowEditModal(false);
            setCurrentExpense(null);
            resetForm();
        };

        if (!showCreateModal && !showEditModal) return null;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
                    <div className="p-6 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <h2 className="text-xl font-semibold text-gray-900">{modalTitle}</h2>
                            <button
                                onClick={handleClose}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <XCircle className="w-6 h-6" />
                            </button>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="p-6 max-h-[calc(90vh-120px)] overflow-y-auto">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Basic Information */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>

                                <StableInput
                                    label="Expense Name"
                                    value={recurringExpenseForm.recurrence_name}
                                    onChange={(value) => handleRecurrenceNameChange(value)}
                                    required
                                    placeholder="e.g., Office Rent, Internet Bill, etc."
                                    fieldName="recurrence_name"
                                />

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Expense Type *
                                    </label>
                                    <select
                                        value={recurringExpenseForm.expense_type}
                                        onChange={(e) => updateFormField('expense_type', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                        required
                                    >
                                        <option value="business">Business</option>
                                        <option value="personal">Personal</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Category *
                                    </label>
                                    <select
                                        value={recurringExpenseForm.category}
                                        onChange={(e) => updateFormField('category', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                        required
                                    >
                                        {expenseCategories.map(category => (
                                            <option key={category.id} value={category.id}>
                                                {category.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <StableInput
                                    label="Description"
                                    value={recurringExpenseForm.description}
                                    onChange={(value) => updateFormField('description', value)}
                                    type="textarea"
                                    rows={3}
                                    required
                                    placeholder="Brief description of the expense..."
                                    fieldName="description"
                                />
                            </div>

                            {/* Account Information */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Account Information</h3>

                                <ExpenseAccountDropdown />
                                <PaymentAccountDropdown />

                                <div className="grid grid-cols-2 gap-4">
                                    <StableInput
                                        label="Amount"
                                        type="number"
                                        value={recurringExpenseForm.amount}
                                        onChange={(value) => updateFormField('amount', value)}
                                        required
                                        placeholder="0.00"
                                        min="0"
                                        step="0.01"
                                        fieldName="amount"
                                    />

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Currency
                                        </label>
                                        <select
                                            value={recurringExpenseForm.currency_code}
                                            onChange={(e) => updateFormField('currency_code', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                        >
                                            <option value="INR">INR (₹)</option>
                                            <option value="USD">USD ($)</option>
                                            <option value="EUR">EUR (€)</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Recurrence Settings */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Recurrence Settings</h3>

                                <div className="grid grid-cols-2 gap-4">
                                    <StableInput
                                        label="Start Date"
                                        type="date"
                                        value={recurringExpenseForm.start_date}
                                        onChange={(value) => updateFormField('start_date', value)}
                                        required
                                        fieldName="start_date"
                                    />

                                    <StableInput
                                        label="End Date (Optional)"
                                        type="date"
                                        value={recurringExpenseForm.end_date}
                                        onChange={(value) => updateFormField('end_date', value)}
                                        fieldName="end_date"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Frequency *
                                        </label>
                                        <select
                                            value={recurringExpenseForm.recurrence_frequency}
                                            onChange={(e) => {
                                                updateFormField('recurrence_frequency', e.target.value);
                                                if (e.target.value !== 'quarterly') {
                                                    updateFormField('repeat_every', 1);
                                                }
                                            }}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                            required
                                        >
                                            <option value="daily">Daily</option>
                                            <option value="weekly">Weekly</option>
                                            <option value="monthly">Monthly</option>
                                            <option value="quarterly">Quarterly</option>
                                            <option value="yearly">Yearly</option>
                                        </select>
                                    </div>

                                    {recurringExpenseForm.recurrence_frequency !== 'quarterly' && (
                                        <StableInput
                                            label="Repeat Every"
                                            type="number"
                                            value={recurringExpenseForm.repeat_every}
                                            onChange={(value) => updateFormField('repeat_every', value)}
                                            min="1"
                                            max="12"
                                            placeholder="1"
                                            fieldName="repeat_every"
                                        />
                                    )}
                                </div>
                            </div>

                            {/* Additional Settings */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Settings</h3>

                                <div className="space-y-3">
                                    <label className="flex items-center space-x-3">
                                        <input
                                            type="checkbox"
                                            checked={recurringExpenseForm.is_billable}
                                            onChange={(e) => updateFormField('is_billable', e.target.checked)}
                                            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">Billable to client</span>
                                    </label>

                                    <label className="flex items-center space-x-3">
                                        <input
                                            type="checkbox"
                                            checked={recurringExpenseForm.is_reimbursable}
                                            onChange={(e) => updateFormField('is_reimbursable', e.target.checked)}
                                            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">Reimbursable</span>
                                    </label>

                                    <label className="flex items-center space-x-3">
                                        <input
                                            type="checkbox"
                                            checked={recurringExpenseForm.receipt_required}
                                            onChange={(e) => updateFormField('receipt_required', e.target.checked)}
                                            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">Receipt required</span>
                                    </label>

                                    <label className="flex items-center space-x-3">
                                        <input
                                            type="checkbox"
                                            checked={recurringExpenseForm.auto_submit}
                                            onChange={(e) => updateFormField('auto_submit', e.target.checked)}
                                            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">Auto submit</span>
                                    </label>
                                </div>

                                {/* Optional Fields */}
                                {(recurringExpenseForm.is_billable || recurringExpenseForm.is_reimbursable) && (
                                    <div className="space-y-4 pt-4 border-t border-gray-200">
                                        {recurringExpenseForm.is_reimbursable && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Employee
                                                </label>
                                                <select
                                                    value={recurringExpenseForm.employee_id}
                                                    onChange={(e) => {
                                                        const employee = employees.find(emp => emp.employee_id === e.target.value);
                                                        updateFormField('employee_id', e.target.value);
                                                        updateFormField('employee_name', employee?.employee_name || '');
                                                    }}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                                >
                                                    <option value="">Select Employee</option>
                                                    {employees.map(employee => (
                                                        <option key={employee.employee_id} value={employee.employee_id}>
                                                            {employee.employee_name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}

                                        {recurringExpenseForm.is_billable && (
                                            <>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Customer
                                                    </label>
                                                    <select
                                                        value={recurringExpenseForm.customer_id}
                                                        onChange={(e) => {
                                                            const customer = customers.find(cust => cust.customer_id === e.target.value);
                                                            updateFormField('customer_id', e.target.value);
                                                            updateFormField('customer_name', customer?.customer_name || '');
                                                        }}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                                    >
                                                        <option value="">Select Customer</option>
                                                        {customers.map(customer => (
                                                            <option key={customer.customer_id} value={customer.customer_id}>
                                                                {customer.customer_name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Project (Optional)
                                                    </label>
                                                    <select
                                                        value={recurringExpenseForm.project_id}
                                                        onChange={(e) => {
                                                            const project = projects.find(proj => proj.project_id === e.target.value);
                                                            updateFormField('project_id', e.target.value);
                                                            updateFormField('project_name', project?.project_name || '');
                                                        }}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                                    >
                                                        <option value="">Select Project</option>
                                                        {projects.map(project => (
                                                            <option key={project.project_id} value={project.project_id}>
                                                                {project.project_name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}

                                <StableInput
                                    label="Notes (Optional)"
                                    value={recurringExpenseForm.notes}
                                    onChange={(value) => updateFormField('notes', value)}
                                    type="textarea"
                                    rows={3}
                                    placeholder="Additional notes or comments..."
                                    fieldName="notes"
                                />
                            </div>
                        </div>

                        {/* Form Actions */}
                        <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={handleClose}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-400 flex items-center space-x-2"
                            >
                                {loading && <Loader className="w-4 h-4 animate-spin" />}
                                <span>{isEditing ? 'Update' : 'Create'} Expense</span>
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    };

    // Delete Confirmation Modal
    const DeleteModal = () => {
        if (!showDeleteModal || !currentExpense) return null;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-lg max-w-md w-full">
                    <div className="p-6">
                        <div className="flex items-center mb-4">
                            <AlertTriangle className="w-6 h-6 text-red-600 mr-3" />
                            <h2 className="text-lg font-semibold text-gray-900">Delete Recurring Expense</h2>
                        </div>
                        <p className="text-gray-600 mb-6">
                            Are you sure you want to delete "{currentExpense.recurrence_name}"? This action cannot be undone.
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => deleteRecurringExpense(currentExpense.id)}
                                disabled={loading}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:bg-gray-400 flex items-center space-x-2"
                            >
                                {loading && <Loader className="w-4 h-4 animate-spin" />}
                                <span>Delete</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Sync Modal
    const SyncModal = () => {
        if (!showSyncModal) return null;

        const expensesToSync = selectedExpenses.length > 0
            ? recurringExpenses.filter(exp => selectedExpenses.includes(exp.id))
            : [];

        const readyToSync = expensesToSync.filter(exp => {
            const validation = validateRecurringExpense(exp);
            const canSyncExpense = canSync(exp);
            return validation.isValid && !exp.zoho_recurring_expense_id && canSyncExpense;
        });

        const notReady = expensesToSync.filter(exp => {
            const validation = validateRecurringExpense(exp);
            const canSyncExpense = canSync(exp);
            return !validation.isValid || exp.zoho_recurring_expense_id || !canSyncExpense;
        });

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden">
                    <div className="p-6 border-b border-gray-200">
                        <h2 className="text-xl font-semibold text-gray-900">Sync Recurring Expenses to Zoho Books</h2>
                    </div>

                    <div className="p-6 max-h-[60vh] overflow-y-auto">
                        {syncProgress ? (
                            <div className="text-center py-8">
                                <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-purple-600" />
                                <p className="text-lg font-medium text-gray-900 mb-2">{syncProgress.status}</p>
                                {syncProgress.total > 0 && (
                                    <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                                        <div
                                            className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                                            style={{ width: `${(syncProgress.current / syncProgress.total) * 100}%` }}
                                        />
                                    </div>
                                )}
                                <p className="text-sm text-gray-600">
                                    {syncProgress.current} of {syncProgress.total} processed
                                </p>
                            </div>
                        ) : (
                            <>
                                {readyToSync.length > 0 && (
                                    <div className="mb-6">
                                        <h3 className="text-lg font-medium text-green-700 mb-3 flex items-center">
                                            <CheckCircle className="w-5 h-5 mr-2" />
                                            Ready to Sync ({readyToSync.length})
                                        </h3>
                                        <div className="space-y-2">
                                            {readyToSync.map(expense => (
                                                <div key={expense.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                                                    <div>
                                                        <p className="font-medium text-gray-900">{expense.recurrence_name}</p>
                                                        <p className="text-sm text-gray-600">₹{expense.amount?.toLocaleString()} • {expense.recurrence_frequency}</p>
                                                    </div>
                                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {notReady.length > 0 && (
                                    <div className="mb-6">
                                        <h3 className="text-lg font-medium text-red-700 mb-3 flex items-center">
                                            <XCircle className="w-5 h-5 mr-2" />
                                            Cannot Sync ({notReady.length})
                                        </h3>
                                        <div className="space-y-2">
                                            {notReady.map(expense => {
                                                const validation = validateRecurringExpense(expense);
                                                const canSyncExpense = canSync(expense);
                                                let reason = '';

                                                if (expense.zoho_recurring_expense_id) {
                                                    reason = 'Already synced';
                                                } else if (!canSyncExpense) {
                                                    const status = expense.approver_status;
                                                    if (status === 'pending_approval') {
                                                        reason = 'Pending approval';
                                                    } else if (status === 'rejected') {
                                                        reason = 'Rejected';
                                                    } else {
                                                        reason = 'Not approved';
                                                    }
                                                } else if (!validation.isValid) {
                                                    reason = `Missing: ${validation.missingFields.join(', ')}`;
                                                }

                                                return (
                                                    <div key={expense.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                                                        <div>
                                                            <p className="font-medium text-gray-900">{expense.recurrence_name}</p>
                                                            <p className="text-sm text-red-600">{reason}</p>
                                                        </div>
                                                        <XCircle className="w-5 h-5 text-red-600" />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {expensesToSync.length === 0 && (
                                    <div className="text-center py-8">
                                        <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                        <h3 className="text-lg font-medium text-gray-900 mb-2">No expenses selected</h3>
                                        <p className="text-gray-600">Please select expenses to sync from the manage page.</p>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
                        <button
                            onClick={() => setShowSyncModal(false)}
                            disabled={!!syncProgress}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:bg-gray-100"
                        >
                            {syncProgress ? 'Close when complete' : 'Cancel'}
                        </button>
                        {readyToSync.length > 0 && !syncProgress && (
                            <button
                                onClick={() => {
                                    syncRecurringExpenses(readyToSync.map(exp => exp.id));
                                }}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
                            >
                                <RefreshCw className="w-4 h-4" />
                                <span>Sync {readyToSync.length} Expenses</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // Validation Modal
    const ValidationModal = () => {
        if (!showValidationModal || !currentExpense) return null;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-lg max-w-md w-full">
                    <div className="p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Validation Results</h2>
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-700">Completion:</span>
                                <span className={`text-sm font-bold ${validationResults.completionPercentage === 100 ? 'text-green-600' : 'text-red-600'}`}>
                                    {validationResults.completionPercentage}%
                                </span>
                            </div>

                            {validationResults.missingFields?.length > 0 && (
                                <div>
                                    <p className="text-sm font-medium text-red-700 mb-2">Missing Fields:</p>
                                    <ul className="list-disc list-inside space-y-1">
                                        {validationResults.missingFields.map((field, index) => (
                                            <li key={index} className="text-sm text-red-600">{field}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {validationResults.warnings?.length > 0 && (
                                <div>
                                    <p className="text-sm font-medium text-yellow-700 mb-2">Warnings:</p>
                                    <ul className="list-disc list-inside space-y-1">
                                        {validationResults.warnings.map((warning, index) => (
                                            <li key={index} className="text-sm text-yellow-600">{warning}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {validationResults.isValid && (
                                <div className="flex items-center space-x-2 text-green-600">
                                    <CheckCircle className="w-5 h-5" />
                                    <span className="text-sm font-medium">Ready for sync!</span>
                                </div>
                            )}
                        </div>
                        <div className="mt-6 flex justify-end space-x-3">
                            <button
                                onClick={() => setShowValidationModal(false)}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                                Close
                            </button>
                            {!validationResults.isValid && (
                                <button
                                    onClick={() => {
                                        setShowValidationModal(false);
                                        handleEdit(currentExpense);
                                    }}
                                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                                >
                                    Fix Issues
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Main render
    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">Recurring Expenses Manager</h1>
                    <p className="text-gray-600 mt-2">Manage and sync your recurring expenses with Zoho Books</p>
                </div>

                {/* Tabs */}
                <div className="border-b border-gray-200 mb-8">
                    <nav className="-mb-px flex space-x-8">
                        <button
                            onClick={() => setActiveTab('overview')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'overview'
                                ? 'border-purple-500 text-purple-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            Overview
                        </button>
                        <button
                            onClick={() => setActiveTab('manage')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'manage'
                                ? 'border-purple-500 text-purple-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            Manage Expenses
                        </button>
                    </nav>
                </div>

                {/* Tab Content */}
                {activeTab === 'overview' && <OverviewDashboard />}
                {activeTab === 'manage' && <ManageExpenses />}
            </div>

            {/* Modals */}
            <CreateEditModal />
            <DeleteModal />
            <SyncModal />
            <ValidationModal />
        </div>
    );
};

export default RecurringExpensesIntegrationManager;